<script setup>
import { computed } from 'vue';

/**
 * Money collected and refunded under each payment method.
 *
 * Every method is listed whether or not it moved money in the window, so a method with no
 * activity reads as zero instead of looking as though it was left out. The legacy summary counts
 * Discover in its totals but never gives it a column.
 */
const props = defineProps({
  /** [{ key, label, collected, refunded, net, share, paymentCount, refundCount }]. */
  methods: {
    type: Array,
    default: () => [],
  },
  /** Wallet cuts through the methods: [{ key, label, fundingBrands, collected, paymentCount }]. */
  wallets: {
    type: Array,
    default: () => [],
  },
});

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const number = new Intl.NumberFormat('en-US');

const collectedTotal = computed(() => props.methods.reduce((sum, row) => sum + row.collected, 0));
const refundedTotal = computed(() => props.methods.reduce((sum, row) => sum + row.refunded, 0));
const peak = computed(() => Math.max(1, ...props.methods.map(row => row.collected)));

/**
 * A wallet's share of everything collected.
 * @param {object} wallet Wallet row.
 * @returns {number}
 */
function walletShare(wallet) {
  return collectedTotal.value > 0 ? (wallet.collected / collectedTotal.value) * 100 : 0;
}
</script>

<template>
  <div :class="$style.wrap">
    <h4 :class="$style.subHead">
      How it was paid
    </h4>

    <div :class="$style.scroller">
      <table :class="$style.table">
        <thead>
          <tr>
            <th scope="col">
              Method
            </th>
            <th scope="col" :class="$style.barCol">
              <span :class="$style.hidden">Share of collected</span>
            </th>
            <th scope="col">
              Payments
            </th>
            <th scope="col">
              Collected
            </th>
            <th scope="col">
              Refunded
            </th>
            <th scope="col">
              Net
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="method in methods"
:key="method.key"
:class="method.collected || method.refunded ? '' : $style.idle"
>
            <th scope="row">
              {{ method.label }}
            </th>
            <td :class="$style.barCol">
              <span :class="$style.track">
                <span :class="$style.bar" :style="{ width: `${(method.collected / peak) * 100}%` }" />
              </span>
            </td>
            <td>{{ number.format(method.paymentCount) }}</td>
            <td>{{ money.format(method.collected) }}</td>
            <td :class="method.refunded ? $style.negative : ''">
              {{ method.refunded ? `-${money.format(method.refunded)}` : money.format(0) }}
            </td>
            <td :class="$style.strong">
              {{ money.format(method.net) }}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">
              All methods
            </th>
            <td :class="$style.barCol" />
            <td>{{ number.format(methods.reduce((sum, row) => sum + row.paymentCount, 0)) }}</td>
            <td>{{ money.format(collectedTotal) }}</td>
            <td :class="refundedTotal ? $style.negative : ''">
              {{ refundedTotal ? `-${money.format(refundedTotal)}` : money.format(0) }}
            </td>
            <td>{{ money.format(collectedTotal - refundedTotal) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>

    <ul v-if="wallets.length" :class="$style.wallets">
      <li v-for="wallet in wallets"
:key="wallet.key"
:class="$style.wallet"
>
        <span>
          of which <strong>{{ wallet.label }}</strong>
          <span v-if="wallet.fundingBrands.length" :class="$style.subtle">
            ({{ wallet.fundingBrands.join(', ') }})
          </span>
        </span>
        <span :class="$style.walletValue">
          {{ money.format(wallet.collected) }}
          <span :class="$style.subtle">
            · {{ number.format(wallet.paymentCount) }} · {{ walletShare(wallet).toFixed(1) }}%
          </span>
        </span>
      </li>
    </ul>

    <p :class="$style.note">
      Apple Pay is charged through Authorize.Net and recorded under the card network that funded
      it, so it is already counted in Visa, Mastercard, American Express or Discover above.
      Where the processor returns no network the payment shows as Unknown.
    </p>
  </div>
</template>

<style module>
.wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}
.subHead {
  margin: 8px 0 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--faint-color);
}
.scroller {
  overflow-x: auto;
  contain: inline-size;
}
.table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-lg);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.table th,
.table td {
  padding: 7px 10px;
  text-align: right;
  border-bottom: 1px solid var(--border-color-light);
}
.table thead th {
  color: var(--faint-color);
  font-weight: 500;
  font-size: var(--font-size-md);
}
.table th:first-child {
  text-align: left;
  font-weight: 500;
  color: var(--primary-font-dark-color);
}
.table tfoot th,
.table tfoot td {
  font-weight: 700;
  color: var(--primary-font-dark-color);
  border-top: 2px solid var(--border-color);
  border-bottom: none;
}
.idle th,
.idle td {
  color: var(--faint-color);
}
.hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
.barCol {
  width: 28%;
  min-width: 90px;
}
.track {
  display: block;
  height: 8px;
  border-radius: 3px;
  background: var(--background-color);
  overflow: hidden;
}
.bar {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: var(--pfs-teal);
  opacity: 0.6;
}
.strong {
  font-weight: 600;
  color: var(--primary-font-dark-color);
}
.negative {
  color: var(--button-red);
}
.wallets {
  list-style: none;
  margin: 2px 0 0;
  padding: 8px 0 0;
  border-top: 1px dashed var(--border-color-light);
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.wallet {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: var(--font-size-md);
  color: var(--primary-font-color);
}
.walletValue {
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.subtle {
  color: var(--faint-color);
}
.note {
  margin: 0;
  font-size: var(--font-size-md);
  color: var(--faint-color);
}
@media (max-width: 640px) {
  .barCol {
    display: none;
  }
}
</style>
